import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { MemberAccount } from 'src/app/models/transactions/member-account';
import { AccountDomainModel } from '../../../../domainModels/account-domain-model';
import { ToastService } from '../../../../services/toast-service';
import { AccountTypeIdEnum } from '../../../../models/enum/shared/account-type-id.enum';
import { MemberUser } from '../../../../models/users/dto/member-user';
import { MemberAccountBalanceByFund } from '../../../../models/transactions/member-account-balance-by-fund';
import { finalize, take } from 'rxjs/operators';

@Injectable()
export class ChangeInvestmentInstructionsSubmissionFormViewModel {
  private accountDomainModel = inject(AccountDomainModel);
  private toastService = inject(ToastService);

  // TODO: Add more account types when available
  private _definedContributionAccounts = new BehaviorSubject<MemberAccount[]>([]);
  public definedContributionAccounts$ = this._definedContributionAccounts as Observable<MemberAccount[]>;

  private _definedContributionAccountBalances = new BehaviorSubject<MemberAccountBalanceByFund[]>([]);
  public definedContributionAccountBalances$ = this._definedContributionAccountBalances.exists();

  private _variableBenefitAccounts = new BehaviorSubject<MemberAccount[]>([]);
  public variableBenefitAccounts$ = this._variableBenefitAccounts.exists();

  private _variableBenefitAccountBalances = new BehaviorSubject<MemberAccountBalanceByFund[]>([]);
  public variableBenefitAccountBalances$ = this._variableBenefitAccountBalances.exists();

  private _user = new BehaviorSubject<MemberUser | null>(null);
  public user$ = this._user as Observable<MemberUser | null>;

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading as Observable<boolean>;

  public setUser(user: MemberUser): void {
    this._user.next(user);
    this.loadAccountData(user.id);
  }

  private loadAccountData(memberId: number): void {
    this._isLoading.next(true);
    forkJoin({
      /* eslint-disable rxjs/finnish  */
      accounts: this.accountDomainModel.ipGetMemberAccounts(memberId, { excludeStruck: true }),
      dcAccountBalances: this.accountDomainModel.ipGetMemberBalancesByAccountAndFund(
        memberId,
        AccountTypeIdEnum.DefinedContribution
      ),
      vbAccountBalances: this.accountDomainModel.ipGetMemberBalancesByAccountAndFund(
        memberId,
        AccountTypeIdEnum.VariableBenefit
      )
      /* eslint-enable rxjs/finnish  */
    })
      .pipe(
        take(1),
        finalize(() => this._isLoading.next(false))
      )
      .subscribe({
        next: ({ accounts, vbAccountBalances, dcAccountBalances }) => {
          const definedContributionAccounts = accounts?.filter(a => a.typeId === AccountTypeIdEnum.DefinedContribution);
          const variableBenefitAccounts = accounts?.filter(a => a.typeId === AccountTypeIdEnum.VariableBenefit);
          this._definedContributionAccounts.next(definedContributionAccounts);
          this._variableBenefitAccounts.next(variableBenefitAccounts);
          this._definedContributionAccountBalances.next(dcAccountBalances);
          this._variableBenefitAccountBalances.next(vbAccountBalances);
        },
        error: error => {
          this.toastService.publishErrorMessage('Error', error);
        }
      });
  }
}
